import React, { useState } from "react";
import { Button, Modal, Nav, Tab } from "react-bootstrap";
import WhiteButton from "../Common/WhiteButton";
import SaveIcon from "../../assets/icons/save.svg2";
import SetupIcon from "../../assets/icons/Icon_Library/application icons/monitoring/setup-01.svg2";
import ConfigureIcon from "../../assets/icons/Icon_Library/application icons/monitoring/configuration-01.svg2";
import MonitoringIcon from "../../assets/icons/monitoring/monitoring-01.svg2";
import WeatherConditionIcon from "../../assets/icons/weather_conditions_icons/weather_conditions.svg2";
import LogsIcon from "../../assets/icons/docs.svg2";
import CogIcon from "../../assets/icons/cog.svg2";
import DetailsIcon from "../../assets/icons/Icon_Library/application icons/assets/events/details-01.svg2";
import MyIcon from "../Common/MyIcon";
import MonitoringSetup, { whatToMonitorOptions } from "./MonitoringSetup";
import MonitoringConfigure from "./MonitoringConfigure";
import MonitoringComplete from "./MonitoringComplete";
import MonitoringDetails from "./MonitoringDetails";
import UpgradesIcon from "../../assets/icons/upgrades.svg2";
import MonitoringConnect from "./MonitoringConnect";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import WeatherAlertConfiguration from "./WeatherAlertConfiguration";
import WeatherAlertCondition from "./WeatherAlertCondition";
import { toast } from "react-toastify";
import { proxyAPI } from "../proxyAPI";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { makeid, shortUUID } from "../helpers";
import { useUpdateMonitorMutation } from "../store/slices/monitoringSlice";
import MonitoringLogs from "./MonitoringLogs";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { h } from "vue";
import HelpButton from "../HelpContent/HelpButton";

const tabs = [
  {
    name: "setup",
    heading: "Setup",
    subheading: (
      <span>
        Create a monitoring workflow by selecting what to track, <br />
        defining alert conditions, and choosing how notifications will be sent
      </span>
    ),
    icon: SetupIcon,
    helpKey: "monitor-setup",
    visible: [
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "weather",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
  {
    name: "weather-configuration",
    heading: "Weather Configuration",
    subheading:
      "Configure weather monitoring by selecting location for alert settings.",
    icon: ConfigureIcon,
    helpKey: "monitor-weather-configuration",
    visible: ["weather"],
  },

  {
    name: "weather-alert-condition",
    heading: "Weather Conditions",
    subheading:
      "Set weather parameters and rules for alerts, including components, values, and how many days ahead to check—up to six days..",
    icon: WeatherConditionIcon,
    helpKey: "monitor-weather-alert-condition",
    visible: ["weather"],
  },
  // {
  //   name: "weather-selected-component",
  //   heading: "Select Component",
  //   subheading: "Lorem ipsum dolor sit amet consectetur adipisicing elit.",
  //   icon: SetupIcon,
  //   visible: ["weather"],
  // },

  {
    name: "configure",
    heading: "Selection",
    subheading: (
      <span>
        Select what you'd like to monitor and specify <br />
        how the monitoring connection will be established.
      </span>
    ),
    icon: ConfigureIcon,
    helpKey: "monitor-configure",
    visible: [
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
  {
    name: "frequency",
    heading: "Configure",
    subheading: (
      <span>
        Configure how monitoring data is processed: pass through all data or
        <br />
        create parsing rules to filter, interpret & transform incoming data.
      </span>
    ),
    icon: DetailsIcon,
    helpKey: "monitor-frequency",
    visible: [
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
  {
    name: "connect",
    heading: "Connect",
    subheading: (
      <span>
        Select the component to connect your monitor to, <br /> enabling it to
        trigger workflows, create reports, and perform automated actions.
      </span>
    ),
    icon: UpgradesIcon,
    helpKey: "monitor-connect",
    visible: [
      "weather",
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
  {
    name: "logs",
    heading: "Logs",
    subheading: (
      <span>
        Review detailed logs of monitor activities, <br />
        including status changes, alerts triggered, and actions taken.
      </span>
    ),
    icon: LogsIcon,
    helpKey: "monitor-logs",
    visible: [
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "weather",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
  {
    name: "summary",
    heading: "Summary",
    subheading: (
      <span>
        Your monitor has been successfully saved and <br /> is now actively
        tracking according to your chosen settings.
      </span>
    ),
    icon: CogIcon,
    helpKey: "monitor-summary",
    visible: [
      "Infrastructure",
      "Mobile Assets",
      "Human Resource",
      "weather",
      "welfare",
      "remoteDevicesAndLocations",
      "environnemental conditions",
      "ping",
      "heartbeat",
      "alert",
    ],
  },
];

function CreateNewMonitorModal() {
  const { monitoringID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const handleClose = () => {
    if (!!location.state?.previousLocationPathname) {
      navigate(-1);
      return;
    }
    navigate("/monitoring");
  };
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [loading, setLoading] = useState(false);
  const [monitor, setMonitor] = useState(null);
  const [monitorBroadcasts, setMonitorBroadcasts] = useState({});
  const [selectedApiDetails, setSelectedApiDetails] = useState();
  const [backendErrors, setBackendErrors] = useState();
  const availableTabs = tabs
    .filter((t) => t.visible.includes(monitor?.Kind))
    .filter((t) => !(t.name === "logs" && !monitor?.Uuid));
  const currentTabIndex = availableTabs.findIndex(
    (t) => t.name === selectedTab.name
  );

  const [updateMonitor] = useUpdateMonitorMutation();

  const [method, setMethod] = useState(null);

  const saveBroadcast = async (values, sendList) => {
    setLoading(true);
    const sendlistUUID = shortUUID();
    const broadcastUUID = values.Uuid || shortUUID();

    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "broadcasters",
      method: "POST",
      body: {
        ...values,
        Uuid: broadcastUUID,
        SendList: sendlistUUID,
        Kind: "monitor",
      },
    });
    if (data) {
      toast.success("Template saved successfully.");
    }

    if (sendList) {
      const sendlistResponse = await proxyAPI({
        prefix: "contacts/v1",
        method: "POST",
        url: "/upsert-send-list",
        body: {
          ...sendList,
          uuid: sendlistUUID,
          projectUuid: "1",
        },
      });
      if (sendlistResponse) {
        toast.success("Template contacts saved successfully.");
      }
    }

    setLoading(false);
    return broadcastUUID;
  };

  const handleSubmit = async () => {
    setBackendErrors(null);

    const m = { ...monitor };

    for (const b in monitorBroadcasts) {
      if (monitorBroadcasts[b]?.broadcast) {
        const uuid = await saveBroadcast(
          {
            ...monitorBroadcasts[b].broadcast,
          },
          monitorBroadcasts[b].sendList
        );
        if (b === "communication") {
          m.BroadcasterCommunicationTpl = uuid;
        }
        if (b === "asset") {
          m.BroadcasterAssetTpl = uuid;
        }
      }
    }

    const res = await updateMonitor(m);

    if (res?.error) {
      console.log(res.error.data.errors);
      setBackendErrors(res.error.data.errors);
      return;
    }
    if (res?.data) {
      const data = { ...res.data };
      if (data.ApiConnection) {
        setSelectedApiDetails(data.ApiConnection);
        delete data.ApiConnection;
      }
      if (data.ApiConnectionUuid) {
        setMethod("selectAnApi");
      }
      setMonitor(data);
      toast.success("Monitor Saved Successfully");
      navigate(`/monitoring/${data.Uuid}`, { replace: true });
    }
    // handleClose();
  };

  const fetchMonitor = async () => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/monitor/rules/${monitoringID}`,
      method: "GET",
    });
    setLoading(false);

    if (data?.error) {
      toast.error(data.error);
      return;
    }

    if (data?.ApiConnection) {
      setSelectedApiDetails(data.ApiConnection);
      delete data.ApiConnection;
    }
    if (data.ApiConnectionUuid) {
      setMethod("selectAnApi");
    }

    if (data.BroadcasterCommunicationTpl) {
      const broadcastdata = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "broadcasters/" + data.BroadcasterCommunicationTpl,
      });
      if (broadcastdata) {
        setMonitorBroadcasts((p) => ({
          ...p,
          communication: { broadcast: broadcastdata },
        }));
        if (broadcastdata.SendList) {
          const sendList = await proxyAPI({
            prefix: "contacts/v1",
            url: `/send-list/${broadcastdata.SendList}`,
          });
          if (sendList) {
            setMonitorBroadcasts((p) => ({
              ...p,
              communication: { ...p.communication, sendList },
            }));
          }
        }
      }
    }

    if (data.BroadcasterAssetTpl) {
      const broadcastdata = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "broadcasters/" + data.BroadcasterAssetTpl,
      });
      if (broadcastdata) {
        setMonitorBroadcasts((p) => ({
          ...p,
          asset: {
            broadcast: broadcastdata,
            sendList: { assets: data.WithAssets },
          },
        }));
      }
    }

    setMonitor({ Priority: "LOW", ...data });
  };

  const handleEnter = async (e) => {
    if (monitoringID === "new") {
      // new monitor
      setMonitor({
        Priority: "low",
        Status: "draft",
        Reference: makeid(8),
        IsComponent: false,
        MonitorPings: [
          {
            Uuid: "", // existing uuid or leave empty when creating
            PingKind: "liveness", // or recovery
            IsActive: true,
            Path: "",
            Timeout: 10,
            SuccessThreshold: 1,
            FailureThreshold: 3,
            SuccessCodes: [200],
            MatchBodyMethod: "ignore", // "ignore", "contains"
            MatchBody: "",
            Headers: {},
            FrequencyKind: "interval", // "fixed"
            IntervalValue: 1,
            IntervalUnit: "minute",
            Hours: [], // empty = every hour when kind is "fixed"
            Minutes: [], // empty = every minute when kind is "fixed"
            WeekDays: [0, 1, 2, 3, 4, 5, 6],
            IgnoreDates: [],
            InitialDelay: 1,
          },
          {
            Uuid: "", // existing uuid or leave empty when creating
            PingKind: "recovery", // or recovery
            IsActive: false,
            Path: "",
            Timeout: 10,
            SuccessThreshold: 1,
            FailureThreshold: 3,
            SuccessCodes: [200],
            MatchBodyMethod: "ignore", // "ignore", "contains"
            MatchBody: "",
            Headers: {},
            FrequencyKind: "interval", // "fixed"
            IntervalValue: 1,
            IntervalUnit: "minute",
            Hours: [], // empty = every hour when kind is "fixed"
            Minutes: [], // empty = every minute when kind is "fixed"
            WeekDays: [0, 1, 2, 3, 4, 5, 6],
            OnDates: [],
            InitialDelay: 1,
          },
        ],
      });
      return;
    }
    // existing monitor
    await fetchMonitor();
  };

  // Error checking to be done when next button is clicked
  const errors = {
    setup: {},
    configure: {},
    frequency: {},
    connect: {},
    logs: {},
    summary: {},
    "weather-configuration": {},
    "weather-alert-condition": {},
  };
  if (monitor?.Kind && !monitor?.Label) {
    errors["setup"]["Monitor.Label"] = "You need to enter a title.";
  }
  if (monitor?.Kind && !monitor?.Description) {
    errors["setup"]["Monitor.Description"] = "You need to enter a description.";
  }

  if (
    monitor?.Kind === "ping" &&
    !monitor?.ApiConnectionUuid &&
    !monitor?.ApiConnection
  ) {
    errors["configure"]["Monitor.ApiConnection"] =
      "You need to select an API connection or create a new one.";
  }

  if (
    monitor?.Kind === "ping" &&
    monitor?.ApiConnection &&
    !monitor?.ApiConnection?.Profile?.BaseUrl
  ) {
    errors["configure"]["Monitor.ApiConnection"] =
      "You need to enter a base URL.";
  }

  if (monitor?.Kind === "weather" && !monitor?.WeatherAlert?.CountryIso) {
    errors["weather-configuration"]["Monitor.WeatherAlert.CountryIso"] =
      "You need to select a country.";
  }

  if (monitor?.Kind === "weather" && !monitor?.WeatherAlert?.GeoUnitType) {
    errors["weather-configuration"]["Monitor.WeatherAlert.GeoUnitType"] =
      "You need to select area type.";
  }

  if (
    monitor?.Kind === "weather" &&
    monitor?.WeatherAlert?.GeoUnitType === "region" &&
    !monitor?.WeatherAlert?.GroupName
  ) {
    errors["weather-configuration"][
      "Monitor.WeatherAlert.selectedCountyRegion"
    ] = "You need to select region.";
  }

  if (
    monitor?.Kind === "weather" &&
    monitor?.WeatherAlert?.GeoUnitType === "county" &&
    !monitor?.WeatherAlert?.ShapeId
  ) {
    errors["weather-configuration"][
      "Monitor.WeatherAlert.selectedCountyRegion"
    ] = "You need to select county.";
  }

  if (monitor?.Kind === "weather" && !monitor?.WeatherAlert?.Description) {
    errors["weather-configuration"]["Monitor.WeatherAlert.description"] =
      "You need to enter a description.";
  }

  if (
    monitor?.Kind === "weather" &&
    !monitor?.WeatherAlert?.Conditions?.length
  ) {
    errors["weather-alert-condition"]["Monitor.WeatherAlert.conditions"] =
      "You need to create at least one condition.";
  }

  if (
    monitor?.Kind === "weather" &&
    monitor?.WeatherAlert?.Conditions?.some((c) => !c.Description)
  ) {
    errors["weather-alert-condition"]["Monitor.WeatherAlert.conditions"] =
      "You need to enter a description for condition.";
  }

  if (
    monitor?.Kind === "weather" &&
    monitor?.WeatherAlert?.Conditions?.some((c) =>
      c.ConditionDetails?.some(
        (cd) => !cd.Condition || !cd.ConditionValue?.length
      )
    )
  ) {
    errors["weather-alert-condition"]["Monitor.WeatherAlert.conditions"] =
      "You need to give condition name and value.";
  }

  return (
    <>
      <FullScreenLoadingIndicator loading={loading} />
      <Modal
        show={monitoringID}
        onHide={handleClose}
        size="xxl"
        contentClassName="bg-purple-gradient"
        onEnter={handleEnter}
      >
        <Modal.Header
          className="bg-colored-reverse p-3 rounded-3 rounded-bottom-0"
          closeButton
        >
          <Modal.Title className="text-white">
            <MonitoringIcon width={30} className="text-white ms-2" />
            <span className="ms-3 fw-bold">
              {" "}
              Create a Monitor <HelpButton helpKey="create-monitor" />
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          {monitor && (
            <>
              <Tab.Container
                id="monitoring-tabs"
                activeKey={selectedTab.name}
                onSelect={(tab) =>
                  setSelectedTab(tabs.find((t) => t.name === tab))
                }
                mountOnEnter
              >
                <Nav variant="tabs" className="flex-nowrap align-items-end">
                  <div className="w-50 d-flex me-5 mb-3 align-items-center">
                    <div className="fw-bold">
                      <MyIcon
                        Icon={selectedTab.icon}
                        width={40}
                        className="ms-2 "
                      />
                    </div>
                    <div className="ms-4">
                      <div className="fs-2 fw-bold text-capitalize">
                        {selectedTab.heading}{" "}
                        {selectedTab.helpKey && (
                          <HelpButton helpKey={selectedTab.helpKey} />
                        )}
                      </div>
                      <span className="text-secondary ">
                        {selectedTab.subheading}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow-1" />
                  {availableTabs.map((t, i) => {
                    const isDisabled = availableTabs.some(
                      (tab, index) =>
                        index < i &&
                        errors[tab.name] &&
                        Object.keys(errors[tab.name]).length > 0
                    );

                    const isSelected = selectedTab.name === t.name;
                    // if (["logs"].includes(t.name) && !monitor.Uuid) {
                    //   return null;
                    // }
                    return (
                      <Nav.Item key={t.name}>
                        <Nav.Link
                          eventKey={t.name}
                          className="fw-bold"
                          disabled={isDisabled}
                          style={{
                            opacity: isSelected ? 1 : 0.5,
                            backgroundColor: "none",
                            "--bs-nav-tabs-link-active-bg": "none",
                            border: "none",
                          }}
                        >
                          <div
                            className={`text-center border-bottom border-3 pb-1 ${
                              isSelected ? "border-purple" : "border-secondary"
                            }`}
                          >
                            <t.icon width={20} className="text-purple" />
                            {!!monitor.WeatherAlert?.Projects?.length &&
                              t.name === "connect" &&
                              !isDisabled && (
                                <span className="badge bg-success ms-2">
                                  {monitor.WeatherAlert?.Projects?.length}
                                </span>
                              )}
                            <div
                              className="text-black fw-bold  mt-2 text-uppercase"
                              style={{ letterSpacing: 2 }}
                            >
                              {t.heading}
                            </div>
                          </div>
                        </Nav.Link>
                      </Nav.Item>
                    );
                  })}
                </Nav>
                <Tab.Content className="p-4 mx-5">
                  <Tab.Pane eventKey="setup">
                    <MonitoringSetup
                      monitor={monitor}
                      setMonitor={setMonitor}
                      backendErrors={backendErrors}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="configure">
                    <MonitoringConfigure
                      monitor={monitor}
                      setMonitor={setMonitor}
                      backendErrors={backendErrors}
                      setSelectedApiDetails={setSelectedApiDetails}
                      method={method}
                      setMethod={setMethod}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="weather-configuration">
                    <WeatherAlertConfiguration
                      weatherAlertData={monitor.WeatherAlert}
                      setWeatherAlertData={(WeatherAlert) =>
                        setMonitor({ ...monitor, WeatherAlert })
                      }
                      errors={backendErrors}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="weather-alert-condition">
                    <WeatherAlertCondition
                      weatherAlertData={monitor.WeatherAlert}
                      setWeatherAlertData={(WeatherAlert) =>
                        setMonitor({ ...monitor, WeatherAlert })
                      }
                      errors={backendErrors}
                    />
                  </Tab.Pane>
                  {/* <Tab.Pane eventKey="weather-selected-component">
                    <WeatherAlertProjects
                      weatherAlertData={monitor.WeatherAlert}
                      setWeatherAlertData={(WeatherAlert) =>
                        setMonitor({ ...monitor, WeatherAlert })
                      }
                    />
                  </Tab.Pane> */}
                  <Tab.Pane eventKey="frequency">
                    <MonitoringDetails
                      monitor={monitor}
                      setMonitor={setMonitor}
                      selectedApiDetails={selectedApiDetails}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="connect">
                    <MonitoringConnect
                      monitor={monitor}
                      setMonitor={setMonitor}
                      monitorBroadcasts={monitorBroadcasts}
                      setMonitorBroadcasts={setMonitorBroadcasts}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="logs">
                    <MonitoringLogs
                      monitor={monitor}
                      fetchMonitor={fetchMonitor}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="summary">
                    <MonitoringComplete
                      errors={Object.values(errors).flat()}
                      monitor={monitor}
                      setMonitor={setMonitor}
                      selectedApiDetails={selectedApiDetails}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
              <div className="d-flex justify-content-between p-3 py-0 mx-5">
                <div>
                  {availableTabs.indexOf(selectedTab) > 0 && (
                    <Button
                      type="submit"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                      onClick={() =>
                        setSelectedTab(
                          availableTabs[availableTabs.indexOf(selectedTab) - 1]
                        )
                      }
                    >
                      PREVIOUS
                    </Button>
                  )}
                </div>
                <div className="d-flex gap-3">
                  {availableTabs.indexOf(selectedTab) + 1 !==
                    availableTabs.length && (
                    <Button
                      type="submit"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                      disabled={!!errors[selectedTab.name]?.length}
                      onClick={() => {
                        const e = Object.entries(
                          errors[selectedTab.name]
                        ).reduce((a, [k, value]) => ({ ...a, [k]: value }), {});

                        console.log(errors, e);
                        setBackendErrors(e);
                        if (Object.keys(e).length > 0) {
                          toast.error("Please fix errors before proceeding.");
                          return;
                        }

                        setSelectedTab(
                          availableTabs[availableTabs.indexOf(selectedTab) + 1]
                        );
                      }}
                    >
                      NEXT
                    </Button>
                  )}

                  {monitor.Kind &&
                    availableTabs.indexOf(selectedTab) + 1 ===
                      availableTabs.length && (
                      <div className="d-flex gap-2">
                        <Button
                          className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                          onClick={handleSubmit}
                        >
                          SAVE
                        </Button>
                      </div>
                    )}
                </div>
              </div>
              <JsonCollapse
                json={{ monitor, monitorBroadcasts }}
                label="Monitor JSON"
              />
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CreateNewMonitorModal;
